import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { Shield, Download, RefreshCw, Trash2, Loader2, Archive, FileSpreadsheet } from "lucide-react";

// iter170 — Admin-only Backup & Recovery. Reuses the existing api helper.
// All heavy lifting is on the server; this page just polls status and
// renders a table + download button.

function StatusBadge({ status }) {
  const map = {
    VERIFIED: "bg-emerald-100 text-emerald-800 border-emerald-200",
    PENDING: "bg-sky-100 text-sky-800 border-sky-200",
    FAILED: "bg-rose-100 text-rose-800 border-rose-200",
  };
  return (
    <span className={`inline-flex text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${map[status] || map.PENDING}`}>
      {status}
    </span>
  );
}

function humanDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN",
      { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function BackupRecovery() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  // iter173 — separate loading flag for the CSV data export.
  const [exportingCsv, setExportingCsv] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/backup/list");
      setRows(data.backups || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Poll while any backup is pending.
    const t = setInterval(() => {
      if (rows.some((r) => r.status === "PENDING")) refresh();
    }, 2000);
    return () => clearInterval(t);
  }, [refresh, rows]);

  const createBackup = async () => {
    setCreating(true);
    try {
      await api.post("/backup/create");
      toast.success("Backup started — this usually takes a few seconds");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  const download = (id, name) => {
    // Server records the download when this URL is hit. Use a real anchor so
    // the browser handles the ZIP save-as dialog.
    const token = localStorage.getItem("token") || "";
    // We can't set Authorization header on an <a> download, so we build a
    // one-shot fetch → Blob → anchor download instead.
    (async () => {
      try {
        const res = await fetch(`${API}/backup/${id}/download`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = name || `Kahani-Studio-Backup-${id.slice(0,8)}.zip`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
        toast.success("Downloaded — the weekly reminder will clear now");
        refresh();
      } catch (e) {
        toast.error(e.message);
      }
    })();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this backup package? Production data will NOT be touched.")) return;
    try {
      await api.delete(`/backup/${id}`);
      toast.success("Backup deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  // iter173 — CSV data export. Streams a ZIP-of-CSVs from the server.
  const exportCsv = async () => {
    setExportingCsv(true);
    const token = localStorage.getItem("token") || "";
    try {
      const res = await fetch(`${API}/backup/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`CSV export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `kahani-csv-export-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
      toast.success("CSV export downloaded");
    } catch (e) {
      toast.error(e.message || "CSV export failed. Please try again.");
    } finally {
      setExportingCsv(false);
    }
  };

  const latestVerified = rows.find((r) => r.status === "VERIFIED");

  return (
    <div className="p-6 space-y-6" data-testid="backup-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-serif text-stone-900 flex items-center gap-2">
            <Shield className="w-5 h-5" strokeWidth={1.6} /> Backup &amp; Recovery
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Create and download a complete backup of your Kahani Studio system. AI Gallery photos and videos are intentionally excluded.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn btn-outline btn-sm"
            onClick={refresh}
            data-testid="backup-refresh"
            title="Refresh list"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={createBackup}
            disabled={creating}
            data-testid="backup-create-btn"
          >
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
            Create Full Backup
          </button>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="backup-summary">
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Last Backup</div>
          <div className="mt-1 text-sm font-serif text-stone-800">
            {latestVerified ? humanDate(latestVerified.created_at) : "None yet"}
          </div>
          {latestVerified && (
            <div className="text-xs text-stone-500">
              {latestVerified.size_human} · SHA-256 {latestVerified.sha256?.slice(0, 12)}…
            </div>
          )}
        </div>
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Last Download</div>
          <div className="mt-1 text-sm font-serif text-stone-800">
            {latestVerified?.downloads?.length
              ? humanDate(latestVerified.downloads[latestVerified.downloads.length - 1].at)
              : "Never"}
          </div>
        </div>
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Records in latest backup</div>
          <div className="mt-1 text-sm font-serif text-stone-800">
            {latestVerified?.manifest?.database?.total_records ?? "—"}
          </div>
        </div>
      </section>

      {/* iter173 — Data Export (CSV). Additive, separate from ZIP backup. */}
      <section className="rounded-lg border border-stone-200 bg-white p-4" data-testid="data-export-section">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-serif text-lg text-stone-800 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4" strokeWidth={1.6} /> Data Export
            </h2>
            <p className="text-xs text-stone-500 mt-1 max-w-xl">
              Download every business collection as separate CSV files inside one ZIP — ready for Excel or Google Sheets. Sensitive credential fields are redacted. AI Gallery photos/videos are NOT included.
            </p>
          </div>
          <button
            className="btn btn-outline btn-sm"
            onClick={exportCsv}
            disabled={exportingCsv}
            data-testid="data-export-csv-btn"
          >
            {exportingCsv ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Export All CSV
          </button>
        </div>
      </section>

      <section className="rounded-lg border border-stone-200 bg-white overflow-hidden">
        <header className="px-4 py-3 border-b border-stone-200 bg-stone-50">
          <h2 className="font-serif text-lg text-stone-800">Backup History</h2>
        </header>
        {loading ? (
          <div className="p-6 text-center text-stone-500 text-sm">
            <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-stone-500 text-sm">
            No backups yet. Click <b>Create Full Backup</b> above.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="backup-table">
            <thead className="bg-stone-50 text-stone-500 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Size</th>
                <th className="text-right px-4 py-2">Records</th>
                <th className="text-left px-4 py-2">Downloads</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-stone-100" data-testid={`backup-row-${r.id}`}>
                  <td className="px-4 py-2 font-mono text-stone-700">{humanDate(r.created_at)}</td>
                  <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.size_human || "—"}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {r.manifest?.database?.total_records ?? "—"}
                  </td>
                  <td className="px-4 py-2">
                    {r.downloads?.length ? `${r.downloads.length}×` : "—"}
                    {r.downloads?.length ? (
                      <span className="ml-1 text-[10px] text-stone-500">
                        last {humanDate(r.downloads[r.downloads.length - 1].at)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-right space-x-1">
                    {r.status === "VERIFIED" && (
                      <button
                        className="btn btn-outline btn-sm !py-0.5 !text-[11px]"
                        onClick={() => download(r.id)}
                        data-testid={`backup-download-${r.id}`}
                      >
                        <Download className="w-3 h-3" /> Download
                      </button>
                    )}
                    <button
                      className="btn btn-outline btn-sm !py-0.5 !text-[11px] text-rose-700 border-rose-200 hover:bg-rose-50"
                      onClick={() => del(r.id)}
                      data-testid={`backup-delete-${r.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
